import { graphql } from '@/utils/graphql';
import { logger } from '@/utils/logger';
import * as A from '@/api';
import Dataset from '@/services/dataset';
import { API, graphqlOperation } from 'aws-amplify';
import { onUpdateGenerativeDashboard } from '@/graphql/subscriptions';
import type Observable from 'zen-observable-ts';
import type { ZenObservable } from 'zen-observable-ts';

const ns = 'app.services.GenerativeDashboard';

export type GenerativeDashboardStatus = 'GENERATING' | 'READY' | 'ERROR';

// One past edit-version entry, kept in DDB as a JSON-encoded array on
// `editVersions`. Each represents the dashboard's renderContent state
// BEFORE a corresponding user edit landed, so the most recent edit's
// pre-state is the last array element. Reverting to an entry restores
// that state and truncates the array.
export type EditVersion = {
  id: string;
  createdAt: string;
  prompt: string;
  summary: string;
  renderContent: string;
};

// One entry in the published-version timeline the version sidebar shows,
// oldest first.
export type PublishedVersion = {
  id: string;
  publishedAt: string;
  renderContent: string;
};

// One row in the sibling GenerativeDashboardVersion DDB table. Versions
// were moved out of inline JSON arrays on the main dashboard row to
// escape the 400KB DDB item limit — each version row gets its own 400KB
// envelope. `kind` is the discriminator: 'initial' (FINALIZE), 'edit'
// (each successful edit), 'publish' (each user-driven publish). The
// content (renderContent) is populated for preview; it's stripped from
// the listing query (versions) to keep the list-versions response small
// for the sidebar.
export type DashboardVersion = {
  id: string;
  dashboardId: string;
  workspaceId: string;
  kind: 'initial' | 'edit' | 'publish';
  createdAt: string;
  updatedAt?: string;
  publishedAt?: string;
  // Edit metadata (null for initial / publish)
  prompt?: string;
  summary?: string;
  // Content — populated on getVersion(), omitted from listVersions()
  renderContent?: string;
  renderContentKey?: string | null;
};

export default class GenerativeDashboard {
  readonly id: string;
  readonly name: string;
  readonly workspaceId: string;
  readonly prompt: string;
  readonly conversationHistory: unknown[];
  readonly renderContent: string | null;
  // S3 pointer for the draft render content, kept alongside the resolved text so
  // publish() can hand the POINTER to the published baseline instead of copying the
  // bytes into the row. Null on legacy rows whose content was only ever inline.
  readonly renderContentKey: string | null;
  readonly publishedRenderContent: string | null;
  // The authoritative "has this ever been published?" signal. `publishedRenderContent`
  // is resolved from S3 and is transiently null while that fetch is in flight, so a
  // guard that reads it can mistake "still loading" for "never published"; the pointer
  // is on the row itself and is therefore stable. (BEEM-2231)
  readonly publishedRenderContentKey: string | null;
  readonly editVersions: EditVersion[];
  readonly status: GenerativeDashboardStatus;
  readonly errorMessage: string | null;
  readonly progressStep: string | null;
  // 'direct' when an AI Assistant dashboard turn wrote the page.
  readonly generator: string | null;
  readonly sampleData: boolean;
  // The turn that built it, when AI Insights created it: its chat starts from that conversation.
  readonly buildTurnId: string | null;
  // The AI Assistant conversation that created it, when it did.
  readonly sourceConversationId: string | null;
  readonly createdBy: string;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  // S3 pointer to the anonymized PNG preview the beemDashboardThumbnailer lambda renders after a
  // publish. Signed like any other artifact. Null until the first render lands.
  readonly thumbnailKey: string | null;

  private constructor(input: Record<string, unknown>) {
    this.id = input.id as string;
    this.name = input.name as string;
    this.workspaceId = input.workspaceId as string;
    this.prompt = input.prompt as string;
    const rawHistory = input.conversationHistory;
    this.conversationHistory = Array.isArray(rawHistory)
      ? rawHistory
      : typeof rawHistory === 'string'
      ? (() => {
          try {
            return JSON.parse(rawHistory);
          } catch {
            return [];
          }
        })()
      : [];
    this.renderContent = (input.renderContent as string) || null;
    this.renderContentKey = (input.renderContentKey as string) || null;
    // Reflect the DB value as-is. A null `publishedRenderContent`
    // means "never published yet" — distinct from "published the same
    // content that's currently on renderContent". Consumers in the
    // View handle the null case explicitly (e.g. View mode falls back
    // to renderContent in `activeRenderContent`, hasUnpublishedChanges
    // treats null as "always allow publish").
    this.publishedRenderContent = (input.publishedRenderContent as string) || null;
    this.publishedRenderContentKey = (input.publishedRenderContentKey as string) || null;
    const rawVersions = input.editVersions;
    this.editVersions = Array.isArray(rawVersions)
      ? (rawVersions as EditVersion[])
      : typeof rawVersions === 'string'
      ? (() => {
          try {
            return JSON.parse(rawVersions);
          } catch {
            return [];
          }
        })()
      : [];
    this.status = (input.status as GenerativeDashboardStatus) || 'GENERATING';
    this.errorMessage = (input.errorMessage as string) || null;
    this.progressStep = (input.progressStep as string) || null;
    this.generator = ((input as Record<string, unknown>).generator as string) ?? null;
    this.sampleData = (input as Record<string, unknown>).sampleData === true;
    this.buildTurnId = ((input as Record<string, unknown>).buildTurnId as string) ?? null;
    this.sourceConversationId =
      ((input as Record<string, unknown>).sourceConversationId as string) ?? null;
    this.createdBy = input.createdBy as string;
    this.createdAt = new Date(input.createdAt as string);
    this.updatedAt = new Date(input.updatedAt as string);
    this.thumbnailKey = (input.thumbnailKey as string) || null;
  }

  // ─── S3 artifact resolution ───
  // Large blobs (renderContent, publishedRenderContent) live ONLY in the customer
  // account's S3 bucket; the DDB row carries just an `<field>Key` pointer and
  // the inline column is never written (BEEM-2231). For each field, mint a
  // presigned GET via getGenerativeDashboardArtifactUrl and fetch the content
  // (the object is gzip + Content-Encoding:gzip, so fetch() auto-decompresses).
  // Resolution merges the fetched strings back onto the raw record so the
  // existing constructor parses them unchanged. A failure leaves the field null
  // rather than throwing — the View tolerates null content.
  private static readonly ARTIFACT_FIELDS: ReadonlyArray<readonly [string, string]> = [
    ['renderContent', 'renderContentKey'],
    ['publishedRenderContent', 'publishedRenderContentKey'],
  ];

  private static readonly artifactUrlQuery = `query GetGenerativeDashboardArtifactUrl($query: GetGenerativeDashboardArtifactUrlInput!) {
    getGenerativeDashboardArtifactUrl(query: $query) { url }
  }`;

  // Resolve `<field>Key` pointers on any record into their `<field>` text.
  //
  // `dashboardId` is passed explicitly rather than read off `raw.id` because
  // this also serves GenerativeDashboardVersion rows, whose `id` is the VERSION
  // id — the presign resolver keys its target lookup on the DASHBOARD row (it
  // reads the customer bucket/role from there), so a version row must supply
  // its `dashboardId`, not its own id.
  //
  // `cache` (optional): keyed by S3 key string → resolved text. The same key
  // always maps to the same content, so a subscription that fires many times
  // during generation only fetches each artifact once (and skips entirely when
  // the key is unchanged between events).
  private static async resolveArtifactFields(
    raw: Record<string, unknown>,
    fields: ReadonlyArray<readonly [string, string]>,
    dashboardId: string,
    workspaceId: string,
    cache?: Map<string, string>,
  ): Promise<Record<string, unknown>> {
    const out: Record<string, unknown> = { ...raw };
    await Promise.all(
      fields.map(async ([field, keyField]) => {
        const key = out[keyField];
        if (typeof key !== 'string' || key.length === 0) return; // no pointer
        if (cache && cache.has(key)) {
          out[field] = cache.get(key);
          return;
        }
        try {
          const res = (await API.graphql(
            graphqlOperation(GenerativeDashboard.artifactUrlQuery, {
              query: { dashboardId, workspaceId, artifactKey: key },
            }),
          )) as { data?: { getGenerativeDashboardArtifactUrl?: { url?: string } } };
          const url = res?.data?.getGenerativeDashboardArtifactUrl?.url;
          if (!url) return; // empty url => missing object; leave the field null
          const text = await (await fetch(url)).text();
          out[field] = text;
          if (cache) cache.set(key, text);
        } catch (err) {
          logger.debug({
            label: `${ns}.resolveArtifacts.error`,
            message: err instanceof Error ? err.message : String(err),
          });
          // leave the field null — the View tolerates null content
        }
      }),
    );
    return out;
  }

  // The thumbnail's presigned URL, loaded straight into an `<img>` (the CSP's img-src admits the
  // S3 host). The URL is signed for 900s, so callers must not hold it much longer than that; the
  // model refetches stale ones. Null when there is no thumbnail yet or no URL could be signed;
  // callers show a placeholder.
  static async getThumbnailUrl(dashboard: GenerativeDashboard): Promise<string | null> {
    if (!dashboard.thumbnailKey) return null;
    try {
      const res = (await API.graphql(
        graphqlOperation(GenerativeDashboard.artifactUrlQuery, {
          query: {
            dashboardId: dashboard.id,
            workspaceId: dashboard.workspaceId,
            artifactKey: dashboard.thumbnailKey,
          },
        }),
      )) as { data?: { getGenerativeDashboardArtifactUrl?: { url?: string } } };
      return res?.data?.getGenerativeDashboardArtifactUrl?.url || null;
    } catch (err) {
      logger.debug({
        label: `${ns}.getThumbnailUrl.error`,
        message: err instanceof Error ? err.message : String(err),
      });
      return null;
    }
  }

  static async resolveArtifacts(
    raw: Record<string, unknown>,
    cache?: Map<string, string>,
  ): Promise<Record<string, unknown>> {
    if (!raw || typeof raw.id !== 'string' || typeof raw.workspaceId !== 'string') return raw;
    return GenerativeDashboard.resolveArtifactFields(
      raw,
      GenerativeDashboard.ARTIFACT_FIELDS,
      raw.id,
      raw.workspaceId,
      cache,
    );
  }

  // What a version row carries. Narrower than ARTIFACT_FIELDS — a version
  // row has no publishedRenderContent of its own.
  private static readonly VERSION_ARTIFACT_FIELDS: ReadonlyArray<readonly [string, string]> = [
    ['renderContent', 'renderContentKey'],
  ];

  static async getById(id: string): Promise<GenerativeDashboard> {
    logger.info(id, { label: `${ns}.getById.id` });
    const data = await graphql('getGenerativeDashboard', { id });
    // Pass a cache so renderContent + publishedRenderContent (which share the
    // same S3 key right after generation) collapse to a single presign+fetch.
    const resolved = await GenerativeDashboard.resolveArtifacts(
      data as Record<string, unknown>,
      new Map(),
    );
    return new GenerativeDashboard(resolved);
  }

  // Subscribe to AppSync `onUpdateGenerativeDashboard` events, filtered
  // client-side to a single dashboard id. Each matching event delivers
  // the fresh DDB record to the supplied callback; the View page
  // dispatches it straight into redux without re-querying. The
  // unsubscribe handle MUST be retained and called on unmount or when
  // the component stops caring (status flipped READY for both main
  // generation and drill-down) — AppSync subscriptions are billed per
  // open connection per minute.
  //
  // Bypasses the typed `subscribe` helper because the Subscription/
  // Result union types in @/api don't include the GenerativeDashboard
  // entries (auto-codegen drops them); calling Amplify's API.graphql
  // directly keeps this surface untyped but functional. The runtime
  // shape is what GraphQL gives us — the constructor on
  // GenerativeDashboard already tolerates the AWSJSON-string variants
  // of array fields, so the same parsing path applies.
  static subscribeToUpdates(
    dashboardId: string,
    onUpdate: (record: GenerativeDashboard) => void,
  ): ZenObservable.Subscription | null {
    try {
      // Cache resolved artifacts by S3 key across the many events a single
      // generation fires, so an unchanged renderContent/wiring key isn't
      // re-fetched on every progress tick.
      const artifactCache = new Map<string, string>();
      const rs = API.graphql(graphqlOperation(onUpdateGenerativeDashboard)) as Observable<{
        value: { data: { onUpdateGenerativeDashboard?: Record<string, unknown> | null } };
      }>;
      return rs.subscribe({
        next: (event) => {
          const record = event?.value?.data?.onUpdateGenerativeDashboard;
          if (!record) return;
          if (record.id !== dashboardId) return;
          // Resolve any S3-offloaded blobs before constructing (async; the
          // subscription handler is fire-and-forget). No-op for inline rows.
          GenerativeDashboard.resolveArtifacts(record, artifactCache)
            .then((resolved) => onUpdate(new GenerativeDashboard(resolved)))
            .catch((err) => {
              logger.debug({
                label: `${ns}.subscribeToUpdates.parseError`,
                message: err instanceof Error ? err.message : String(err),
              });
            });
        },
        error: (err) => {
          logger.debug({
            label: `${ns}.subscribeToUpdates.error`,
            message: err instanceof Error ? err.message : String(err),
          });
        },
      });
    } catch (err) {
      logger.debug({
        label: `${ns}.subscribeToUpdates.setupError`,
        message: err instanceof Error ? err.message : String(err),
      });
      return null;
    }
  }

  // Just the progress of a dashboard being built, for the AI Insights card that links to it.
  static async getStatus(id: string): Promise<{ status: string; progressStep: string | null }> {
    const data = await graphql('getGenerativeDashboardOnlyStatus', { id });
    return { status: data?.status || 'GENERATING', progressStep: data?.progressStep || null };
  }

  static async getByWorkspace(workspaceId: string): Promise<GenerativeDashboard[]> {
    logger.info(workspaceId, { label: `${ns}.getByWorkspace.workspaceId` });
    const data = await graphql('listGenerativeDashboardsByWorkspace', { workspaceId });
    return data.items.map((el: Record<string, unknown>) => new GenerativeDashboard(el));
  }

  static async create(input: A.CreateGenerativeDashboardInput): Promise<string> {
    logger.info({ label: `${ns}.create.input`, message: input });
    const data = await graphql('createGenerativeDashboard', { input });
    return data.id;
  }

  static async update(input: A.UpdateGenerativeDashboardInput): Promise<string> {
    logger.info({ label: `${ns}.update.input`, message: input });
    const data = await graphql('updateGenerativeDashboard', { input });
    return data.id;
  }

  static async updateConversationHistory(id: string, history: unknown[]): Promise<void> {
    await graphql('updateGenerativeDashboard', {
      input: { id, conversationHistory: JSON.stringify(history) },
    });
  }

  static async getDatasetContext(workspaceId: string): Promise<string> {
    const ownedDatasets = await Dataset.getDatasetsOwnByWorkspace(workspaceId, undefined, {
      enableSearchByAI: { eq: true },
    });
    let sharedDatasets = await Dataset.getDatasetsSharedWithWorkspace(workspaceId);
    sharedDatasets = sharedDatasets.filter((el) => el.enableSearchByAI);

    const allDatasets = ownedDatasets.concat(sharedDatasets);
    const datasetAttrs = allDatasets.map((dataset) => ({
      id: dataset.dbViewsQualifiedName,
      // Human-friendly dataset name (distinct from the "schema"."view" id) so
      // the rendered dashboard's provenance popover can label the source nicely.
      name: dataset.name,
      description: `${dataset.description}\n${dataset.createTableStatement}`,
    }));

    return JSON.stringify(datasetAttrs);
  }

  // ─── Server-authoritative artifact commits ───
  // publish / revert / discard are POINTER operations on artifacts that already
  // live in the customer account's S3 bucket, so they run server-side via
  // commitGenerativeDashboardArtifacts (BEEM-2231).
  //
  // These used to write the HTML back INLINE through updateGenerativeDashboard,
  // which bypassed S3 entirely: it regressed an offloaded row to DDB-hosted on
  // every publish, put customer content in the App account (where the table
  // lives), and was a latent ItemSizeExceeded on a 150-400KB shell.
  //
  // No content crosses the wire any more — the client names an action and the
  // server reads the authoritative bytes itself. The resolver also writes the
  // kind=publish version row, so the frontend no longer orders those two writes.
  private static readonly commitMutation = `mutation CommitGenerativeDashboardArtifacts($input: CommitGenerativeDashboardArtifactsInput!) {
    commitGenerativeDashboardArtifacts(input: $input) {
      id
      renderContentKey
      publishedRenderContentKey
      editVersions
      conversationHistory
      status
      updatedAt
    }
  }`;

  private static async commit(input: {
    dashboardId: string;
    workspaceId: string;
    action: 'PUBLISH' | 'REVERT_TO_PUBLISHED_VERSION' | 'REVERT_TO_VERSION' | 'DISCARD_DRAFT';
    versionId?: string;
    editVersions?: string;
    conversationHistory?: string;
  }): Promise<void> {
    await API.graphql(graphqlOperation(GenerativeDashboard.commitMutation, { input }));
  }

  // Commit the current draft as the published baseline. The server repoints
  // publishedRenderContentKey at the current renderContent object and writes the
  // kind=publish version row.
  static async publish(id: string, workspaceId: string): Promise<void> {
    logger.info({ label: `${ns}.publish.id`, message: id });
    await GenerativeDashboard.commit({
      dashboardId: id,
      workspaceId,
      action: 'PUBLISH',
    });
  }

  // Revert to a past version from the sibling versions table AND republish it.
  // The server copies that version's artifact objects onto fresh main-row keys,
  // repoints both the draft and the published baseline at them, and writes a NEW
  // kind=publish row capturing the revert ("republished at now") — so history
  // stays append-only and every entry reads as "what was current at this
  // timestamp".
  static async revertToPublishedVersion(
    id: string,
    workspaceId: string,
    versionId: string,
  ): Promise<void> {
    logger.info({ label: `${ns}.revertToPublishedVersion`, message: { id, versionId } });
    await GenerativeDashboard.commit({
      dashboardId: id,
      workspaceId,
      action: 'REVERT_TO_PUBLISHED_VERSION',
      versionId,
    });
  }

  // Discard reverts the dashboard's working state (renderContent) to the last
  // published version and wipes the edit-version history — throwing away every
  // change made since the last publish. Server-side this is a pointer alias
  // (renderContentKey := publishedRenderContentKey), so the caller no longer
  // needs to hand us the published HTML.
  //
  // workspaceId is required for the resolver's workspace authorization.
  static async discardDraft(
    id: string,
    workspaceId: string,
    conversationHistory: unknown[],
  ): Promise<void> {
    logger.info({ label: `${ns}.discardDraft.id`, message: id });
    await GenerativeDashboard.commit({
      dashboardId: id,
      workspaceId,
      action: 'DISCARD_DRAFT',
      conversationHistory: JSON.stringify(conversationHistory),
    });
  }

  // Revert the working DRAFT to a specific past version — the published
  // baseline is untouched. The server copies that version's content onto
  // fresh main-row keys.
  //
  // `truncatedVersions` is the edit-history timeline trimmed to entries that
  // pre-date the chosen version — metadata only (ids, prompts, summaries), no
  // content. Forward-only: re-editing from here appends to the truncated
  // history.
  static async revertToVersion(
    id: string,
    workspaceId: string,
    versionId: string,
    truncatedVersions: EditVersion[],
    conversationHistory: unknown[],
  ): Promise<void> {
    logger.info({ label: `${ns}.revertToVersion`, message: { id, versionId } });
    await GenerativeDashboard.commit({
      dashboardId: id,
      workspaceId,
      action: 'REVERT_TO_VERSION',
      versionId,
      editVersions: JSON.stringify(truncatedVersions),
      conversationHistory: JSON.stringify(conversationHistory),
    });
  }

  // List version-history rows for a dashboard from the sibling
  // GenerativeDashboardVersion table. Returns metadata only — no
  // renderContent — so the sidebar can
  // render the timeline without pulling ~40-100KB per row over the wire.
  // Use getVersion(id) when the user actually clicks Revert to fetch the
  // full content for that one version.
  //
  // sortDirection 'DESC' so the newest entry is first (the sidebar wants
  // recent-on-top). Returns the raw list; callers filter by kind if they
  // only want publishes / edits / initial.
  static async listVersions(dashboardId: string): Promise<DashboardVersion[]> {
    const liteQuery = `query ListGenerativeDashboardVersionsByDashboardLite($dashboardId: ID!, $limit: Int) {
      listGenerativeDashboardVersionsByDashboard(
        dashboardId: $dashboardId
        sortDirection: DESC
        limit: $limit
      ) {
        items {
          id
          dashboardId
          workspaceId
          kind
          createdAt
          updatedAt
          publishedAt
          prompt
          summary
        }
      }
    }`;
    try {
      const res = (await API.graphql(graphqlOperation(liteQuery, { dashboardId, limit: 200 }))) as {
        data?: { listGenerativeDashboardVersionsByDashboard?: { items?: DashboardVersion[] } };
      };
      return res?.data?.listGenerativeDashboardVersionsByDashboard?.items ?? [];
    } catch (e) {
      logger.debug({
        label: `${ns}.listVersions.error`,
        message: e instanceof Error ? e.message : String(e),
      });
      return [];
    }
  }

  // Fetch the full content of a single version row. Used by the Version History
  // PREVIEW path, which needs the actual HTML to render in the iframe.
  //
  // A version row's content lives in the customer bucket behind the *Key
  // pointers (BEEM-2231), so the row is fetched and then resolved. Note that
  // REVERTING no longer goes through here — the server copies the objects
  // directly (see revertToVersion / revertToPublishedVersion), so no dashboard
  // HTML round-trips through the browser just to be written back.
  static async getVersion(versionId: string): Promise<DashboardVersion | null> {
    const fullQuery = `query GetGenerativeDashboardVersion($id: ID!) {
      getGenerativeDashboardVersion(id: $id) {
        id
        dashboardId
        workspaceId
        kind
        createdAt
        publishedAt
        renderContentKey
        prompt
        summary
      }
    }`;
    try {
      const res = (await API.graphql(graphqlOperation(fullQuery, { id: versionId }))) as {
        data?: { getGenerativeDashboardVersion?: DashboardVersion };
      };
      const version = res?.data?.getGenerativeDashboardVersion;
      if (!version) return null;
      const raw = version as unknown as Record<string, unknown>;
      if (typeof raw.dashboardId !== 'string' || typeof raw.workspaceId !== 'string') {
        // Without both ids the presign call cannot be made, so the content stays
        // null and the preview iframe renders blank. Say so rather than failing
        // silently — this shape means the version row was written incorrectly.
        logger.debug({
          label: `${ns}.getVersion.unresolvable`,
          message: `version ${versionId} lacks dashboardId/workspaceId; artifacts cannot be resolved`,
        });
        return version;
      }
      const resolved = await GenerativeDashboard.resolveArtifactFields(
        raw,
        GenerativeDashboard.VERSION_ARTIFACT_FIELDS,
        raw.dashboardId,
        raw.workspaceId,
        new Map(),
      );
      return resolved as unknown as DashboardVersion;
    } catch (e) {
      logger.debug({
        label: `${ns}.getVersion.error`,
        message: e instanceof Error ? e.message : String(e),
      });
      return null;
    }
  }

  static async delete(id: string): Promise<void> {
    logger.info({ label: `${ns}.delete.id`, message: id });
    const widgets = await graphql('listGenerativeDashboardWidgetsByDashboard', {
      dashboardId: id,
    });
    await Promise.all(
      widgets.items.map((widget: { id: string }) =>
        graphql('deleteGenerativeDashboardWidget', { input: { id: widget.id } }),
      ),
    );
    await graphql('deleteGenerativeDashboard', { input: { id } });
  }
}
